/**
 * @pit/sim — movement stepping + collision resolution.
 *
 * NORMATIVE algorithm from wp3-sim-core.md §"Movement":
 *
 *   dx = tx − x; dy = ty − y; dsq = dx² + dy²; s = moveSpeed
 *   if dsq ≤ s²:     candidate = (tx, ty)                    // arrival
 *   else:            candidate = (x, y) + scaleVectorToSpeed(dx, dy, s)
 *   then collision-resolve(candidate):
 *     - clamp to arena boundary (with radius margin)
 *     - vs each pillar (array order), then each OTHER living entity (seat
 *       order): if penetrating, try axis-slide (X-first tie-break), else
 *       stay put.
 *   post-resolution: `arrived` reports whether the candidate reached the
 *   target this tick — callers that track a persistent target (e.g.
 *   `forcedMovement.ts`'s fear/charm authority) use it to decide whether to
 *   keep pushing toward the same point next tick.
 *
 * `scaleVectorToSpeed` (below) is the SHARED integer vector-to-target-speed
 * primitive both `stepToward` and `directionalDelta` (#252's WASD sibling)
 * scale through — see its doc comment for the derivation (#273 fix,
 * SIM_VERSION 0.48.0). It REPLACES the earlier "single floored `isqrt`
 * denominator, then per-axis floored `idiv`" technique: that technique's
 * flooring behavior was documented as an intentional sub-degree DIRECTION
 * bias, but it ALSO understated the shared denominator for small vectors
 * (the common case for a facing direction), inflating BOTH axes by up to
 * `√2` — a genuine 41% MAGNITUDE overshoot, not a rounding nuance. The new
 * primitive floors ONCE per axis, from an exact integer numerator, so
 * `dx²+dy² ≤ s²` holds UNCONDITIONALLY (a theorem, not a tolerance) — see
 * `scaleVectorToSpeed`'s doc comment.
 *
 * All arithmetic is integer via `@pit/math`. NO Math.*, NO `/`, NO `%`
 * (`Math.abs`/ternary sign checks are the sim-purity-approved integer
 * primitives — see `iabs` below, mirroring `facingDir.ts`/`bounce.ts`).
 */

import { idiv, isqrt } from "@pit/math";
import type { PitMap, StrafeMode, Units } from "@pit/protocol";
import {
  clampToArena,
  isPositionLegal,
} from "./collision.js";
import type { DirectionalIntent, Entity, FacingDir, MoveTarget, PhasingFlags, Wall } from "./types.js";

/** Integer absolute value (Math.abs is fine per lint, but the codebase's
 *  convention — `facing.ts`/`facingDir.ts`/`bounce.ts` — is a local `iabs`
 *  ternary so every sim-purity primitive is visibly hand-rolled). */
function iabs(n: number): number {
  return n < 0 ? -n : n;
}

/**
 * The largest absolute component `scaleVectorToSpeed` will divide-and-square
 * without shrinking first. Any input axis exceeding this is halved
 * (direction-preserving — both axes halve together) until it isn't, so the
 * largest intermediate product the primitive ever computes is bounded by
 * `DIR_MAX_COMPONENT² · s²` — with `DIR_MAX_COMPONENT = 2^15`, that's `2^30 ·
 * s²`, comfortably inside `MAX_SAFE_INTEGER` (`2^53`) for any speed up to
 * `2^11` units/tick — far above any sim speed (shipped `moveSpeed` is 23/100;
 * see fixtures.ts). This is what lets an arena-scale delta (`stepToward`'s
 * `tx − x`, up to a few thousand units — arena coords ≤ ~4096) and a small
 * canonical facing vector (`directionalDelta`'s `FacingDir`) share this ONE
 * primitive safely.
 */
const DIR_MAX_COMPONENT = 32768;

/**
 * Scale direction vector `(vx, vy)` to target magnitude `s` — the SHARED
 * primitive behind BOTH `stepToward`'s per-tick step and `directionalDelta`'s
 * facing-relative axis contributions (#273 fix; replaces the old "single
 * floored `isqrt` denominator, then per-axis floored `idiv`" technique).
 *
 * ALGORITHM: strip signs, compute each axis's EXACT squared fraction of `s²`
 * (`ax²·s² / (ax²+ay²)`, un-rounded), and floor ONCE per axis via `isqrt`.
 * Signs are reapplied at the end.
 *
 *   n  = ax² + ay²                          (exact, no rounding)
 *   dx = isqrt(idiv(ax²·s², n)) · sign(vx)
 *   dy = isqrt(idiv(ay²·s², n)) · sign(vy)
 *
 * WHY THIS FIXES THE OVERSHOOT (#273): the old technique floored the shared
 * denominator FIRST (`d = isqrt(ax²+ay²)`), then divided each axis by that
 * floored, UNDERESTIMATED length — for a SMALL vector (e.g. a canonical
 * facing `(-1,-1)`, true length `√2 ≈ 1.414`, floored to `1`) the
 * underestimate inflates BOTH axes by `√2 ≈ 1.414×`, a 41% magnitude
 * overshoot. Small vectors are the COMMON case for a facing direction, not
 * an edge case. This primitive never floors a shared denominator — each
 * axis's floor is the LAST operation on an exact fraction that is itself
 * `≤ s²`, so the result is bounded by construction:
 *
 *   dx² + dy² ≤ s²   for every `(vx, vy, s)` — a theorem, not a tolerance.
 *
 * (Proof sketch: `dx² ≤ idiv(ax²s², n) ≤ ax²s²/n` and likewise for `dy²`;
 * summing gives `dx²+dy² ≤ (ax²+ay²)s²/n = s²`.) Undershoot is bounded to
 * under 1 unit per axis (a single floor each), so ~2 units total — nothing
 * like the old ±41% swing.
 *
 * This ALSO removes the old technique's negative-`idiv`-floor asymmetry
 * (flooring a negative quotient rounds AWAY from zero, e.g. `idiv(-7,2) =
 * -4`): working in absolute value and reapplying the sign at the end means
 * a facing vector and its mirror image produce EXACTLY mirrored output, with
 * no directional skew from sign.
 *
 * `(0, 0)` maps to `(0, 0)` (no direction ⇒ no displacement — `stepToward`'s
 * arrival branch and `directionalDelta`'s always-nonzero `FacingDir`
 * guarantee this is never the ONLY path fed a zero vector, but the check is
 * cheap and keeps this primitive total). `s` is assumed non-negative (both
 * call sites only ever pass a speed).
 */
function scaleVectorToSpeed(
  vx: number,
  vy: number,
  s: number,
): { readonly dx: number; readonly dy: number } {
  if (vx === 0 && vy === 0) return { dx: 0, dy: 0 };
  const sx = vx < 0 ? -1 : 1;
  const sy = vy < 0 ? -1 : 1;
  let ax = iabs(vx);
  let ay = iabs(vy);
  // Overflow guard: shrink the direction (never changes it — both axes
  // halve together) until it's comfortably inside the safe-integer budget
  // for the squarings below. See `DIR_MAX_COMPONENT`'s doc comment.
  while (ax > DIR_MAX_COMPONENT || ay > DIR_MAX_COMPONENT) {
    ax = idiv(ax, 2);
    ay = idiv(ay, 2);
  }
  const n = ax * ax + ay * ay;
  /* c8 ignore start -- unreachable: (vx,vy) !== (0,0) guarantees at least one
   * of ax,ay is positive, and halving a positive integer never reaches 0
   * while the loop condition still allows > DIR_MAX_COMPONENT, so n > 0 here.
   * Defensive against future refactors of the shrink loop. */
  if (n === 0) return { dx: 0, dy: 0 };
  /* c8 ignore stop */
  const dx = isqrt(idiv(ax * ax * s * s, n));
  const dy = isqrt(idiv(ay * ay * s * s, n));
  // Normalize -0 → 0 (same discipline as `idiv`/`imod` in imath.ts): a
  // negative sign times a floored-to-zero magnitude (`sx * 0`) produces `-0`,
  // which diverges from `0` in `Object.is`/some hash paths.
  const rx = sx * dx;
  const ry = sy * dy;
  return { dx: rx === 0 ? 0 : rx, dy: ry === 0 ? 0 : ry };
}

// Exposed via `_internalForTests` (mirrors `facing.ts`'s pattern) so the
// #273 property-test matrix can exercise the shared primitive directly —
// independent of `stepToward`'s arrival early-out and `directionalDelta`'s
// tuning-derived speed values — without a public-API export.
export const _internalForTests: {
  readonly scaleVectorToSpeed: typeof scaleVectorToSpeed;
} = { scaleVectorToSpeed };

// ─── Directional (WASD) movement intent (#252) ────────────────────────────

/** The all-false "no directional intent" value — the stable sentinel/absent
 *  marker for `Entity.moveIntent` (see `types.ts`). */
export const NO_MOVE_INTENT: DirectionalIntent = {
  forward: false,
  backward: false,
  strafeLeft: false,
  strafeRight: false,
};

/** True iff ANY of the four directional flags is held. */
export function hasDirectionalIntent(intent: DirectionalIntent): boolean {
  return intent.forward || intent.backward || intent.strafeLeft || intent.strafeRight;
}

/**
 * #351: fixed world-space "forward"/"right" axis for SCREEN-RELATIVE strafe —
 * derived from the client's projection (`worldToScreen` in
 * `apps/client/src/render/projection.ts`).
 *
 * DERIVATION (against #730's AXIS-ALIGNED basis: `sx = x·ISO_SCALE_X·zoom +
 * cx`, `sy = y·ISO_SCALE_Y·zoom + cy`):
 *   - "purely up"    (Δsx = 0, Δsy < 0) ⇒ `dx = 0, dy < 0` — world N.
 *   - "purely right" (Δsy = 0, Δsx > 0) ⇒ `dy = 0, dx > 0` — world E.
 * Both hold for ANY nonzero `ISO_SCALE_X`/`ISO_SCALE_Y` and any `zoom` (each
 * scale cancels out of its own constraint, and the translation drops out of a
 * DIFFERENCE), so this is the exact screen axis pair regardless of zoom / iso
 * ratio / viewport cover factor — not an approximation, and NOT the
 * sin/cos-rounded `SECTOR_UNIT_DELTAS` facing-snap table (a different table
 * for a different purpose). Unit-length by construction, which keeps
 * `scaleVectorToSpeed`'s exact-fraction split exact with zero approximation
 * error.
 *
 * HISTORY — these were `(-1,-1)`/`(1,-1)` (world NW/NE) until #730. That was
 * correct for the OLD rotated basis (`sx = (x−y)·SX`, `sy = (x+y)·SY`), whose
 * fixed 45° yaw rendered the world diagonals as pure screen axes. #730
 * axis-aligned the basis so the playable area fills the viewport, which
 * rotated the screen axes 45° and left these constants stale — a user who
 * selected "Screen-relative" in Settings would have moved 45° off from the key
 * they pressed. Rotating them here is the fix. Anyone changing the projection
 * basis again MUST re-derive this pair from it.
 */
const SCREEN_FORWARD_AXIS = { x: 0, y: -1 } as const; // world N = screen "up"
const SCREEN_RIGHT_AXIS = { x: 1, y: 0 } as const; // world E = screen "right"

/**
 * Pure directional step (#252; #351 screen-relative mode) — the WASD sibling
 * of `stepToward`. Given the entity's current FACING (an arbitrary non-zero
 * integer vector, NOT limited to the 16 render sectors — see `facingDir.ts`),
 * which of the four directional keys are held, and the STRAFE AXIS CONVENTION
 * (`strafeMode`), compute this tick's raw (uncollided) displacement
 * `(dx, dy)`.
 *
 * `strafeMode === "facing"` (default): forward/backward move ALONG the facing
 * axis; strafe moves PERPENDICULAR to it. "Right" is the facing vector
 * rotated 90° clockwise on-screen: `(fx, fy) -> (-fy, fx)` (screen-Y
 * convention, positive dy is South — the same convention `facing.ts`/
 * `facingDir.ts` use throughout).
 *
 * `strafeMode === "screen"` (#351): forward/right are the FIXED
 * `SCREEN_FORWARD_AXIS`/`SCREEN_RIGHT_AXIS` world vectors above (world N/E
 * since #730), independent of `facing` entirely — `d` always moves
 * screen-right, `a` screen-left, `w`/`s` screen-up/down, regardless of which
 * way the caster faces.
 *
 * Each active axis is scaled INDEPENDENTLY (forward at `forwardSpeed`,
 * backward at `forwardSpeed · backwardSpeedScaleBp / 10000`, strafe at
 * `forwardSpeed · strafeSpeedScaleBp / 10000`) and the contributions are
 * summed, THEN the combined vector is CLAMPED back down to the fastest
 * single active axis speed this tick (#252-fix, SIM_VERSION bump — see
 * below). A diagonal hold (e.g. forward+strafeRight) no longer exceeds
 * plain forward speed: without this clamp, two perpendicular full-speed
 * axis contributions sum to `√2 ≈ 1.41×` either axis alone, a diagonal-strafe
 * speed exploit (the same bug historically seen in Quake-era FPS movement).
 * Reverses the original #252 "no diagonal cap" decision — flagged as a real
 * gameplay bug (2026-07-18), not a design preference.
 *
 * Opposite keys on the same axis (forward+backward, or strafeLeft+strafeRight)
 * cancel to zero on that axis — same net effect as releasing both. #785: this
 * is now a DEFENSIVE FALLBACK rather than the behavior a player sees. The
 * keyboard client no longer sends a both-true axis at all: press order is only
 * known client-side (the wire carries four UNORDERED booleans —
 * `SIM_INPUT_MOVE_DIR_SCHEMA`), so `InputSurface.tsx` resolves each opposing
 * pair to its most recently pressed key before sending (hold w, press s ⇒
 * reverse, not a dead stop). The branch stays because both-true intents remain
 * legal on the wire and are still produced by bots (`tools/arena`) and existing
 * replays — removing it would change sim behavior for those.
 *
 * Integer-only: each axis contribution is scaled to its target speed via the
 * SHARED `scaleVectorToSpeed` primitive (#273 fix — see its doc comment; this
 * is the SAME primitive `stepToward`'s magnitude step now uses). No trig, no
 * float. The clamp-rescale step reuses the SAME primitive (squared-magnitude
 * compare, then a single rescale) — no new/duplicated math.
 *
 * Note (acceleration prep, #252 explicitly out of scope): `forwardSpeed` is
 * threaded through as a single per-tick scalar rather than a hardcoded
 * constant, so a future ramp-up could scale it before calling in — but NO
 * accel/decel state or curve exists here.
 */
export function directionalDelta(
  facing: FacingDir,
  intent: DirectionalIntent,
  forwardSpeed: number,
  strafeSpeedScaleBp: number,
  backwardSpeedScaleBp: number,
  /** #351: default `"facing"` keeps every pre-#351 caller byte-identical. */
  strafeMode: StrafeMode = "facing",
): { readonly dx: number; readonly dy: number } {
  const fx = strafeMode === "screen" ? SCREEN_FORWARD_AXIS.x : facing.fx;
  const fy = strafeMode === "screen" ? SCREEN_FORWARD_AXIS.y : facing.fy;
  const rx = strafeMode === "screen" ? SCREEN_RIGHT_AXIS.x : -facing.fy;
  const ry = strafeMode === "screen" ? SCREEN_RIGHT_AXIS.y : facing.fx;
  let dx = 0;
  let dy = 0;
  // Track the fastest single active axis this tick — the cap ceiling below.
  let maxAxisSpeed = 0;
  if (intent.forward && !intent.backward) {
    const step = scaleVectorToSpeed(fx, fy, forwardSpeed);
    dx += step.dx;
    dy += step.dy;
    if (forwardSpeed > maxAxisSpeed) maxAxisSpeed = forwardSpeed;
  } else if (intent.backward && !intent.forward) {
    const backSpeed = idiv(forwardSpeed * backwardSpeedScaleBp, 10000);
    const step = scaleVectorToSpeed(-fx, -fy, backSpeed);
    dx += step.dx;
    dy += step.dy;
    if (backSpeed > maxAxisSpeed) maxAxisSpeed = backSpeed;
  }
  if (intent.strafeRight && !intent.strafeLeft) {
    const strafeSpeed = idiv(forwardSpeed * strafeSpeedScaleBp, 10000);
    const step = scaleVectorToSpeed(rx, ry, strafeSpeed);
    dx += step.dx;
    dy += step.dy;
    if (strafeSpeed > maxAxisSpeed) maxAxisSpeed = strafeSpeed;
  } else if (intent.strafeLeft && !intent.strafeRight) {
    const strafeSpeed = idiv(forwardSpeed * strafeSpeedScaleBp, 10000);
    const step = scaleVectorToSpeed(-rx, -ry, strafeSpeed);
    dx += step.dx;
    dy += step.dy;
    if (strafeSpeed > maxAxisSpeed) maxAxisSpeed = strafeSpeed;
  }
  // Clamp the combined vector's magnitude down to `maxAxisSpeed` (the
  // fastest single axis active this tick) whenever two perpendicular axes
  // combined would otherwise exceed it — e.g. forward+strafe. Single-axis
  // holds (dx²+dy² already ≈ maxAxisSpeed²) pass through unchanged modulo
  // integer rounding; only genuine diagonal combinations get rescaled.
  const combinedSq = dx * dx + dy * dy;
  const maxSq = maxAxisSpeed * maxAxisSpeed;
  if (combinedSq > maxSq && combinedSq > 0) {
    const rescaled = scaleVectorToSpeed(dx, dy, maxAxisSpeed);
    dx = rescaled.dx;
    dy = rescaled.dy;
  }
  return { dx, dy };
}

/**
 * Peek this tick's INTENDED (uncollided) per-tick displacement for `source` —
 * the shared derivation #149's projectile momentum reads (#563: WASD is the
 * only player movement source, so this is now a thin wrapper over
 * `directionalDelta`):
 *   - directional intent held (any flag true) ⇒ `directionalDelta` (WASD).
 *   - else ⇒ zero vector (stationary / rooted-by-omission caller).
 *
 * Pure; does not consult collision. `moveSpeed` is the caller's ALREADY
 * aura-modified + root-gated speed (0 for a rooted mover, matching the
 * momentum call site's existing `canMove` gate).
 */
export function peekMoveDelta(
  source: Pick<Entity, "facing" | "moveIntent" | "strafeMode">,
  moveSpeed: number,
  strafeSpeedScaleBp: number,
  backwardSpeedScaleBp: number,
): { readonly dx: number; readonly dy: number } {
  if (hasDirectionalIntent(source.moveIntent)) {
    return directionalDelta(
      source.facing,
      source.moveIntent,
      moveSpeed,
      strafeSpeedScaleBp,
      backwardSpeedScaleBp,
      source.strafeMode,
    );
  }
  return { dx: 0, dy: 0 };
}

/**
 * The result of stepping one entity for one tick.
 *
 * - `x, y`: final integer centre after collision resolution.
 * - `arrived`: TRUE iff the entity's stepped candidate reached the target
 *   before collision. Callers that persist a target point (e.g.
 *   `forcedMovement.ts`) use this to clear it on arrival.
 * - `moved`: TRUE iff `(finalX, finalY) !== (oldX, oldY)`. Facing updates
 *   only when this is true — stationary/blocked preserves facing.
 */
export interface StepResult {
  readonly x: Units;
  readonly y: Units;
  readonly arrived: boolean;
  readonly moved: boolean;
}

/**
 * Pure toward-target integer step. NO collision — returns the candidate
 * point per the normative algorithm.
 *
 * Guarantees (each is a required property test):
 *   1. `step² ≤ speed²` — never over-steps, UNCONDITIONALLY (a structural
 *      theorem of `scaleVectorToSpeed`, #273 fix — the pre-fix algorithm
 *      could exceed this by up to `√2` for a diagonal-ish target offset).
 *   2. If not arriving, `distSq(new, target) < distSq(old, target)` —
 *      strictly decreasing squared distance (no orbiting).
 *   3. Pure function of `(x, y, tx, ty, speed)`.
 *
 * `speed === 0` is legal — result is always `(x, y)` with `arrived === true`
 * iff already on target; otherwise `arrived === false`. (Zero-speed entities
 * never move.)
 *
 * NOTE (#273 owner comment, explicit non-goal): the arrival early-out below
 * ("closer to the target than one step away ⇒ land exactly on it") is a
 * caller-specific concern of THIS function — it does NOT belong in the
 * shared `scaleVectorToSpeed` primitive, which has no notion of "arrival" (a
 * `directionalDelta` facing vector is a direction indicator, never literally
 * "remaining distance to travel").
 */
export function stepToward(
  x: number,
  y: number,
  tx: number,
  ty: number,
  speed: number,
): { readonly x: number; readonly y: number; readonly arrived: boolean } {
  const dx = tx - x;
  const dy = ty - y;
  const dsq = dx * dx + dy * dy;
  const s2 = speed * speed;
  if (dsq <= s2) {
    // Arrival: snap exactly to the target. Applies even for speed 0 IFF
    // already at (x,y) === (tx,ty) (then dsq === 0 ≤ 0). Otherwise (speed 0,
    // not on target) dsq > 0 = s2, so we fall through and no movement is
    // produced.
    return { x: tx, y: ty, arrived: true };
  }
  const step = scaleVectorToSpeed(dx, dy, speed);
  return { x: x + step.dx, y: y + step.dy, arrived: false };
}

/**
 * Shared collision resolution for a raw (uncollided) candidate step —
 * boundary clamp, primary legality, then X-first axis slide, then
 * stay-put. Used by BOTH the point-based (`resolveMovement`) and
 * directional/WASD (`resolveDirectionalMovement`) resolvers (#252) so the
 * pillar/entity/wall collision logic is defined in exactly ONE place
 * regardless of which "what direction am I trying to move" input produced
 * the raw candidate.
 *
 * `primaryX`/`primaryY` are the post-boundary-clamp candidate (before any
 * slide) — callers that care about "arrival" (point-based movement only)
 * compare these against the raw step's target-facing coords themselves;
 * this helper does not know about "arrival" (a WASD directional step has no
 * such concept).
 */
function resolveCandidate(
  mover: Entity,
  rawX: number,
  rawY: number,
  moverRadius: number,
  map: PitMap,
  entities: readonly Entity[],
  otherRadii: readonly number[],
  walls: readonly Wall[],
  phasing: readonly PhasingFlags[] | undefined,
): { readonly x: Units; readonly y: Units; readonly primaryLegal: boolean; readonly primaryX: Units; readonly primaryY: Units } {
  const oldX = mover.x;
  const oldY = mover.y;

  // Boundary clamp (with radius margin). This may reduce the step but
  // never pushes back past the old position: `clampToArena` clamps a
  // point independently on each axis.
  const clamped = clampToArena(rawX, rawY, moverRadius, map);
  const candidateX = clamped.x;
  const candidateY = clamped.y;

  // Primary legality: after boundary clamp, is the candidate free of
  // pillar/entity/wall penetration?
  if (
    isPositionLegal(
      candidateX,
      candidateY,
      moverRadius,
      map,
      entities,
      otherRadii,
      mover.seat,
      walls,
      mover.team,
      phasing,
    )
  ) {
    return { x: candidateX, y: candidateY, primaryLegal: true, primaryX: candidateX, primaryY: candidateY };
  }

  // Axis-slide, X-FIRST tie-break (normative; wp3-sim-core.md §"Traps"
  // "don't pick 'the better one'").
  //
  // The slide uses the SAME legality predicate as the primary check —
  // sharing the code path is the anti-tunneling guarantee.
  const xOnlyClamped = clampToArena(candidateX, oldY, moverRadius, map);
  if (
    isPositionLegal(
      xOnlyClamped.x,
      xOnlyClamped.y,
      moverRadius,
      map,
      entities,
      otherRadii,
      mover.seat,
      walls,
      mover.team,
      phasing,
    )
  ) {
    return { x: xOnlyClamped.x, y: xOnlyClamped.y, primaryLegal: false, primaryX: candidateX, primaryY: candidateY };
  }
  const yOnlyClamped = clampToArena(oldX, candidateY, moverRadius, map);
  if (
    isPositionLegal(
      yOnlyClamped.x,
      yOnlyClamped.y,
      moverRadius,
      map,
      entities,
      otherRadii,
      mover.seat,
      walls,
      mover.team,
      phasing,
    )
  ) {
    return { x: yOnlyClamped.x, y: yOnlyClamped.y, primaryLegal: false, primaryX: candidateX, primaryY: candidateY };
  }

  // Fully blocked. Stay put.
  return { x: oldX, y: oldY, primaryLegal: false, primaryX: candidateX, primaryY: candidateY };
}

/**
 * Resolve a single entity's movement for one tick against the CURRENT
 * positions of all other entities in `entities` (seat-order iteration).
 *
 * Mutation contract: this function does NOT mutate anything. Callers
 * apply the returned `(x, y)` to the entity being moved and then move on
 * to the next seat.
 *
 * `otherRadii` is a per-seat radius array (index === seat). Caller
 * builds it once per tick via `getStat` — the mover's own entry is
 * ignored (index `mover.seat` is skipped by the penetration test).
 */
export function resolveMovement(
  mover: Entity,
  moveTarget: MoveTarget,
  moveSpeed: number,
  moverRadius: number,
  map: PitMap,
  entities: readonly Entity[],
  otherRadii: readonly number[],
  /** #129: active walls the mover must respect (default: none — pre-#129
   *  byte-identical). Gated by each wall's `blocksMovement` + `blockedTeams`
   *  against `mover.team` inside `isPositionLegal`. */
  walls: readonly Wall[] = [],
  /** #199: per-seat phasing flags (index === seat). Forwarded to
   *  `isPositionLegal` on every legality probe (primary + both axis slides) so a
   *  phasing mover passes through the geometry it phases and a phasing OTHER
   *  body is passed through. Default: undefined (pre-#199 byte-identical). */
  phasing?: readonly PhasingFlags[],
): StepResult {
  const oldX = mover.x;
  const oldY = mover.y;
  const step = stepToward(oldX, oldY, moveTarget.x, moveTarget.y, moveSpeed);
  const res = resolveCandidate(mover, step.x, step.y, moverRadius, map, entities, otherRadii, walls, phasing);
  const arrived = step.arrived && res.primaryLegal && res.primaryX === step.x && res.primaryY === step.y;
  const moved = res.x !== oldX || res.y !== oldY;
  return { x: res.x, y: res.y, arrived, moved };
}

/**
 * Resolve a single entity's WASD DIRECTIONAL movement for one tick (#252) —
 * the facing-relative sibling of `resolveMovement`. Computes the raw
 * candidate via `directionalDelta` (facing/strafeMode + held keys + speed
 * tunables — #351: `mover.strafeMode` selects the facing- vs. screen-relative
 * axis convention) and resolves it through the EXACT SAME collision path
 * (`resolveCandidate`) as point-based movement — no parallel collision logic.
 * There is no "arrival" concept for a directional hold (no target point), so
 * `arrived` is always `false`.
 */
export function resolveDirectionalMovement(
  mover: Entity,
  intent: DirectionalIntent,
  moveSpeed: number,
  strafeSpeedScaleBp: number,
  backwardSpeedScaleBp: number,
  moverRadius: number,
  map: PitMap,
  entities: readonly Entity[],
  otherRadii: readonly number[],
  /** #129: active walls the mover must respect (default: none). */
  walls: readonly Wall[] = [],
  /** #199: per-seat phasing flags (index === seat). Default: undefined. */
  phasing?: readonly PhasingFlags[],
): StepResult {
  const oldX = mover.x;
  const oldY = mover.y;
  const delta = directionalDelta(
    mover.facing,
    intent,
    moveSpeed,
    strafeSpeedScaleBp,
    backwardSpeedScaleBp,
    mover.strafeMode,
  );
  const res = resolveCandidate(
    mover,
    oldX + delta.dx,
    oldY + delta.dy,
    moverRadius,
    map,
    entities,
    otherRadii,
    walls,
    phasing,
  );
  const moved = res.x !== oldX || res.y !== oldY;
  return { x: res.x, y: res.y, arrived: false, moved };
}
